import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from './AnimatedSection';

// Helper to extract ID from any YouTube URL
const getVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : url;
};

export const OurVision = ({ data }) => {
  if (!data) return null;

  return (
    <section id="our-vision" className="ov-section">
      <div className="container">
        {/* --- INTRO HEADER --- */}
        <AnimatedSection>
          <div className="ov-intro text-center">
            <h2 className="ov-title">{data.intro.title}</h2>
            <div className="ov-divider"></div>
            <p className="ov-intro-text">{data.intro.paragraph}</p>
          </div>
        </AnimatedSection>

        {/* --- CONTENT SECTIONS --- */}
        <div className="ov-content-wrapper">
          {data.sections.map((section, index) => {
            const videoId = section.video ? getVideoId(section.video) : null;

            return (
              <AnimatedSection key={index}>
                <div className={`ov-row ${section.reverse ? 'reverse' : ''}`}>
                  {/* Media Column */}
                  <div className="ov-col-image">
                    <div className="ov-media-frame">
                      {/* Decorative background element for depth */}
                      <div className="ov-media-decoration"></div>

                      <div className="ov-media-content">
                        {videoId ? (
                          <div className="ov-video-wrapper">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&origin=${window.location.origin}`}
                              title={section.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        ) : (
                          <img
                            src={section.image}
                            alt={section.title}
                            className="ov-image"
                            loading="lazy"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Text Column */}
                  <div className="ov-col-text">
                    <div className="ov-text-box">
                      <div className="ov-step-number">0{index + 1}</div>
                      <h3 className="ov-subtitle">{section.title}</h3>
                      <p className="ov-desc">{section.text}</p>

                      <ul className="ov-bullets">
                        {section.bullets.map((point, i) => (
                          <li key={i} className="ov-bullet-item">
                            <div className="ov-icon-box">
                              <i className="fa fa-check"></i>
                            </div>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="ov-actions">
                        {index === 0 && (
                          <Link
                            to="/projects#projects"
                            className="ov-btn-primary"
                          >
                            View Projects <i className="fa fa-arrow-right"></i>
                          </Link>
                        )}

                        {section.reverse && index !== 2 && (
                          <Link
                            to="/idea#bring-idea"
                            className="ov-btn-primary"
                          >
                            Share Your Idea{' '}
                            <i className="fa fa-arrow-right"></i>
                          </Link>
                        )}

                        {index === 2 && (
                          <div className="ov-btn-group">
                            <Link
                              to="/idea#bring-idea"
                              className="ov-btn-primary"
                            >
                              Share Your Idea
                            </Link>
                            <Link to="/how-it-works" className="ov-btn-text">
                              See How it Works
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>

      {/* ==============================================
          CSS STYLES
      ============================================== */}
      <style jsx>{`
        .ov-section {
          padding-top: 120px;
          padding-bottom: 120px;
          background-color: #fff;
          overflow: hidden;
        }

        /* --- INTRO --- */
        .ov-intro {
          max-width: 700px;
          margin: 0 auto 100px;
        }

        .ov-tagline {
          font-family: 'Open Sans', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: #6372ff;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 10px;
          display: block;
        }

        .ov-title {
          font-family: 'Raleway', sans-serif;
          font-size: 42px;
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 25px;
          letter-spacing: -0.5px;
        }

        .ov-divider {
          width: 60px;
          height: 4px;
          background: linear-gradient(to right, #5ca9fb 0%, #6372ff 100%);
          margin: 0 auto 30px;
          border-radius: 4px;
        }

        .ov-intro-text {
          font-family: 'Open Sans', sans-serif;
          font-size: 18px;
          color: #555;
          line-height: 1.8;
        }

        /* --- LAYOUT GRID --- */
        .ov-content-wrapper {
          display: flex;
          flex-direction: column;
          gap: 120px;
        }

        .ov-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 80px;
        }

        .ov-row.reverse {
          flex-direction: row-reverse;
        }

        .ov-col-image {
          flex: 1;
          display: flex;
          justify-content: center;
          position: relative;
        }

        .ov-col-text {
          flex: 1;
        }

        /* --- MEDIA STYLES (Modernized) --- */
        /* This container restricts the image size */
        .ov-media-frame {
          position: relative;
          width: 100%;
          max-width: 480px; /* Limits the image size dramatically */
          z-index: 1;
        }

        /* The decorative offset background */
        .ov-media-decoration {
          position: absolute;
          top: 20px;
          right: -20px;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            135deg,
            rgba(92, 169, 251, 0.15),
            rgba(99, 114, 255, 0.15)
          );
          border-radius: 24px;
          z-index: -1;
          transition: transform 0.4s ease;
        }

        /* Reverse direction decoration offset */
        .reverse .ov-media-decoration {
          right: auto;
          left: -20px;
        }

        .ov-media-content {
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.12); /* Softer, modern shadow */
          background: #fff;
          transform: translateZ(0); /* Hardware acceleration */
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .ov-media-frame:hover .ov-media-content {
          transform: translateY(-5px);
          box-shadow: 0 30px 60px -15px rgba(99, 114, 255, 0.2);
        }

        .ov-media-frame:hover .ov-media-decoration {
          transform: translate(5px, 5px);
        }

        .ov-image {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
        }

        /* Video specific adjustments */
        .ov-video-wrapper {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #000;
        }

        .ov-video-wrapper iframe {
          width: 100%;
          height: 100%;
        }

        /* --- TEXT STYLES --- */
        .ov-step-number {
          font-size: 60px;
          font-weight: 900;
          color: #f0f2f5;
          line-height: 1;
          margin-bottom: -20px;
          margin-left: -5px;
          font-family: 'Raleway', sans-serif;
          position: relative;
          z-index: -1;
        }

        .ov-subtitle {
          font-family: 'Raleway', sans-serif;
          font-size: 32px;
          font-weight: 700;
          color: #222;
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .ov-desc {
          font-family: 'Open Sans', sans-serif;
          font-size: 16px;
          color: #666;
          line-height: 1.8;
          margin-bottom: 30px;
        }

        /* --- BULLETS --- */
        .ov-bullets {
          list-style: none;
          padding: 0;
          margin: 0 0 40px 0;
          display: grid;
          gap: 15px;
        }

        .ov-bullet-item {
          display: flex;
          align-items: center;
          font-size: 15px;
          color: #444;
          font-weight: 500;
        }

        .ov-icon-box {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(99, 114, 255, 0.1);
          color: #6372ff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
          font-size: 12px;
        }

        /* --- BUTTONS --- */
        .ov-actions {
          margin-top: 10px;
        }

        .ov-btn-group {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .ov-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #6372ff 0%, #5ca9fb 100%);
          color: white;
          padding: 14px 32px;
          border-radius: 50px;
          font-weight: 600;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-decoration: none;
          border: none;
          box-shadow: 0 4px 15px rgba(99, 114, 255, 0.3);
          transition: all 0.3s ease;
        }

        .ov-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(99, 114, 255, 0.4);
          color: white;
          text-decoration: none;
        }

        .ov-btn-primary i {
          transition: transform 0.3s;
        }
        .ov-btn-primary:hover i {
          transform: translateX(4px);
        }

        .ov-btn-text {
          color: #555;
          font-weight: 600;
          font-size: 15px;
          text-decoration: none;
          position: relative;
          padding: 5px 0;
        }

        .ov-btn-text:after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: 0;
          left: 0;
          background-color: #6372ff;
          transition: width 0.3s;
        }

        .ov-btn-text:hover {
          color: #6372ff;
          text-decoration: none;
        }
        .ov-btn-text:hover:after {
          width: 100%;
        }

        /* =========================================
           RESPONSIVE
        ========================================= */
        @media (max-width: 992px) {
          .ov-section {
            padding-top: 80px;
          }

          .ov-row,
          .ov-row.reverse {
            flex-direction: column;
            gap: 50px;
            text-align: left;
          }

          .ov-col-image,
          .ov-col-text {
            width: 100%;
            flex: auto;
          }

          /* Ensure image centers on mobile */
          .ov-col-image {
            align-items: center;
          }

          .ov-media-frame {
            max-width: 100%; /* Let it go slightly wider on mobile */
            width: 90%;
          }

          .ov-media-decoration {
            right: -10px;
            top: 10px;
          }

          .reverse .ov-media-decoration {
            left: -10px;
          }

          .ov-step-number {
            display: none; /* Hide large numbers on mobile to save space */
          }
        }

        @media (max-width: 500px) {
          .ov-title {
            font-size: 32px;
          }
          .ov-intro-text {
            font-size: 16px;
          }
          .ov-subtitle {
            font-size: 26px;
          }
          .ov-media-frame {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};
